import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import animationHtml from './animacao.html?raw'
import { ANIMATION_PACE } from './constants'
import styles from './styles.module.css'

const fallbackBrands = [
  {
    key: 'sorvete',
    prompt: 'Gere uma campanha para vender mais sorvete no outono',
    cards: [
      {
        image: '',
      },
      {
        image: '',
      },
      {
        image: '',
      },
      {
        image: '',
      },
      {
        image: '',
        big: true,
      },
    ],
  },
  {
    key: 'hamburguer',
    prompt: 'Gere uma campanha para vender mais hambúrgueres no fim de semana',
    cards: [
      {
        title: 'Sabor de fim de semana',
        subtitle: 'feito para pedir de novo.',
        background: 'linear-gradient(180deg, #3f2d1f 0%, #1d1712 100%)',
      },
      {
        title: 'Ponto da carne',
        subtitle: 'na medida certa.',
        background: 'linear-gradient(180deg, #f0d2a8 0%, #d4a66b 100%)',
      },
      {
        title: 'Molhos intensos',
        subtitle: 'que fecham o apelo visual.',
        background: 'linear-gradient(180deg, #62422f 0%, #35241a 100%)',
      },
      {
        title: 'Combo irresistível',
        subtitle: 'com apetite imediato.',
        background: 'linear-gradient(180deg, #8b5f3e 0%, #3c2213 100%)',
      },
      {
        title: 'O hambúrguer que domina o fim de semana.',
        subtitle: 'Uma campanha com foco em desejo.',
        background: 'linear-gradient(180deg, #2a1e15 0%, #0d0a08 100%)',
        big: true,
      },
    ],
  },
  {
    key: 'perfume',
    prompt: 'Gere uma campanha para lançar nossa nova fragrância',
    cards: [
      {
        title: 'Notas de saída',
        subtitle: 'leves e elegantes.',
        background: 'linear-gradient(180deg, #f7f0ff 0%, #caa8ff 100%)',
      },
      {
        title: 'Corpo da fragrância',
        subtitle: 'mais sofisticado e envolvente.',
        background: 'linear-gradient(180deg, #d8b7ef 0%, #8457b8 100%)',
      },
      {
        title: 'Presença marcante',
        subtitle: 'sem perder delicadeza.',
        background: 'linear-gradient(180deg, #5b3d7d 0%, #22142f 100%)',
      },
      {
        title: 'Detalhe premium',
        subtitle: 'com acabamento refinado.',
        background: 'linear-gradient(180deg, #e8d9f8 0%, #9b74c9 100%)',
      },
      {
        title: 'Uma fragrância criada para ser lembrada.',
        subtitle: 'Campanha pronta para o lançamento.',
        background: 'linear-gradient(180deg, #311d46 0%, #140b1d 100%)',
        big: true,
      },
    ],
  },
  {
    key: 'farmacia',
    prompt: 'Gere uma campanha para promover nossa farmácia',
    cards: [
      {
        title: 'Cuidado que inspira confiança',
        subtitle: 'na rotina de quem compra.',
        background: 'linear-gradient(180deg, #f0f6ff 0%, #9cc2ff 100%)',
      },
      {
        title: 'Promoções da semana',
        subtitle: 'com leitura imediata.',
        background: 'linear-gradient(180deg, #dfeafe 0%, #7ba6e8 100%)',
      },
      {
        title: 'Saúde em destaque',
        subtitle: 'com comunicação limpa.',
        background: 'linear-gradient(180deg, #5274ae 0%, #1a2b4a 100%)',
      },
      {
        title: 'Produtos essenciais',
        subtitle: 'em evidência no feed.',
        background: 'linear-gradient(180deg, #e8effe 0%, #a7badf 100%)',
      },
      {
        title: 'A farmácia com presença forte e acolhedora.',
        subtitle: 'Mensagem clara, visual confiável.',
        background: 'linear-gradient(180deg, #20304f 0%, #0f1728 100%)',
        big: true,
      },
    ],
  },
]

const extractBrands = (rawHtml) => {
  const match = rawHtml.match(/const BRANDS = ([\s\S]*?)\n\s*const stage =/)

  if (!match) {
    return fallbackBrands
  }

  try {
    const parsed = Function(`return ${match[1]}`)()

    return parsed.map((brand) => ({
      key: brand.key,
      prompt: brand.prompt,
      cards: (brand.images || []).map((image, index) => ({
        image,
        big: index === (brand.images || []).length - 1,
      })),
    }))
  } catch {
    return fallbackBrands
  }
}

const brands = extractBrands(animationHtml)

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, Math.round(ms * ANIMATION_PACE)))

export const SignInAnimation = () => {
  const { t } = useTranslation()
  const [activeBrandIndex, setActiveBrandIndex] = useState(0)
  const [typedPrompt, setTypedPrompt] = useState('')
  const [visibleCards, setVisibleCards] = useState(0)
  const [cursorVisible, setCursorVisible] = useState(false)
  const [cursorPhase, setCursorPhase] = useState('idle')
  const [sendPressed, setSendPressed] = useState(false)
  const [cursorTransform, setCursorTransform] = useState('translate3d(0,0,0) scale(1)')
  const [isTyping, setIsTyping] = useState(false)
  const [stageReady, setStageReady] = useState(false)
  const stageRef = useRef(null)
  const sendBtnRef = useRef(null)
  const cursorRef = useRef(null)

  const activeBrand = brands[activeBrandIndex] || brands[0]
  const isTall = activeBrand.prompt.length > 54

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      await sleep(180)
      setStageReady(true)
      let brandIndex = 0

      while (!cancelled) {
        const nextBrandIndex = brandIndex % brands.length
        const brand = brands[nextBrandIndex]
        setActiveBrandIndex(nextBrandIndex)
        setTypedPrompt('')
        setVisibleCards(0)
        setCursorVisible(false)
        setCursorPhase('idle')
        setSendPressed(false)
        setIsTyping(true)

        await sleep(140)

        const chars = [...brand.prompt]
        const totalTypingTime = brand.key === 'hamburguer' ? 1900 : 1650
        const baseDelay = Math.max(22, Math.round(totalTypingTime / chars.length))
        let buffer = ''

        for (let i = 0; i < chars.length; i += 1) {
          if (cancelled) {
            return
          }

          buffer += chars[i]
          setTypedPrompt(buffer)
          const jitter = Math.round((Math.random() - 0.5) * baseDelay * 0.35)
          await sleep(Math.max(18, baseDelay + jitter))
        }

        setIsTyping(false)
        await sleep(120)

        if (cancelled) {
          return
        }

        setCursorVisible(true)
        setSendPressed(true)

        // compute dynamic cursor transform: move from current cursor pos to sendBtn center
        try {
          const stageEl = stageRef.current
          const btnEl = sendBtnRef.current
          const curEl = cursorRef.current
          if (stageEl && btnEl && curEl) {
            const stageRect = stageEl.getBoundingClientRect()
            const btnRect = btnEl.getBoundingClientRect()
            const curRect = curEl.getBoundingClientRect()

            const svgViewBoxSize = 28
            const tipXInViewBox = 5
            const tipYInViewBox = 3
            const tipOffsetX = (tipXInViewBox / svgViewBoxSize) * curRect.width
            const tipOffsetY = (tipYInViewBox / svgViewBoxSize) * curRect.height

            const targetX = btnRect.left + btnRect.width / 2 - stageRect.left - tipOffsetX
            const targetY = btnRect.top + btnRect.height / 2 - stageRect.top - tipOffsetY

            const initialX = curRect.left - stageRect.left - tipOffsetX
            const initialY = curRect.top - stageRect.top - tipOffsetY

            const deltaX = targetX - initialX
            const deltaY = targetY - initialY

            // set transform to translate by the delta; CSS transition will animate it
            setCursorTransform(`translate3d(${Math.round(deltaX)}px, ${Math.round(deltaY)}px, 0) scale(1)`)
            setCursorPhase('moving')
          } else {
            setCursorPhase('moving')
          }
        } catch (e) {
          setCursorPhase('moving')
        }

        await sleep(780)
        setCursorPhase('pressed')
        // scale down slightly when pressed
        setCursorTransform((prev) => prev.replace(/scale\([^)]*\)/, 'scale(0.88)'))
        await sleep(150)
        setSendPressed(false)

        for (let i = 0; i < brand.cards.length; i += 1) {
          if (cancelled) {
            return
          }

          setVisibleCards(i + 1)
          await sleep(110)
        }

        await sleep(1450)
        // reset cursor to original position
        setCursorTransform('translate3d(0,0,0) scale(1)')
        setCursorVisible(false)
        await sleep(180)

        if (cancelled) {
          return
        }

        brandIndex += 1
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div
      className={styles.stageWrapper}
      style={{ '--animation-pace': ANIMATION_PACE }}
      aria-hidden="true"
    >
      <div
        className={`${styles.stage} ${stageReady ? styles.visible : ''}`}
        ref={stageRef}
        data-tall={isTall ? 'true' : 'false'}
      >
        <div
          className={`${styles.inputCard} ${isTall ? styles.inputTall : ''} ${
            typedPrompt ? styles.visibleCard : ''
          } ${isTyping ? styles.typing : ''}`}
        >
          <div className={styles.inputText}>
            <span className={typedPrompt ? '' : styles.placeholder}>
              {typedPrompt || t('signin_animation_placeholder')}
            </span>
            <span className={styles.caret} />
          </div>

          <div className={styles.inputActions}>
            <div className={styles.actionPill}>
              <svg width="14" height="14" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path
                  d="M6.5 3l-3 3a1.5 1.5 0 0 0 2 2l3-3a2.5 2.5 0 0 0-3.5-3.5L2 4.5"
                  stroke="#747474"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
              {t('signin_animation_attach')}
            </div>

            <div ref={sendBtnRef} className={`${styles.sendBtn} ${sendPressed ? styles.pressed : ''}`}>
              <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  d="M17.5158 2.01275C17.9478 0.81775 16.7898 -0.34025 15.5948 0.0927503L0.989804 5.37475C-0.209196 5.80875 -0.354196 7.44475 0.748804 8.08375L5.4108 10.7828L9.5738 6.61975C9.76241 6.43759 10.015 6.3368 10.2772 6.33908C10.5394 6.34135 10.7902 6.44652 10.9756 6.63193C11.161 6.81734 11.2662 7.06815 11.2685 7.33035C11.2708 7.59255 11.17 7.84515 10.9878 8.03375L6.8248 12.1968L9.5248 16.8587C10.1628 17.9618 11.7988 17.8158 12.2328 16.6178L17.5158 2.01275Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className={`${styles.artworks} ${isTall ? styles.shifted : ''}`}>
          {activeBrand.cards.map((card, index) => {
            const isVisible = index < visibleCards
            const cardClassName = card.big ? styles.bigCard : styles.smallCard

            return (
              <div
                key={`${activeBrand.key}-${index}`}
                className={`${styles.art} ${cardClassName} ${
                  isVisible ? styles.artVisible : ''
                }`}
                style={{ background: '#efeaf3' }}
              >
                {card.image ? <img src={card.image} alt="" /> : null}
              </div>
            )
          })}
        </div>

        <div
          ref={cursorRef}
          className={`${styles.cursor} ${cursorVisible ? styles.cursorVisible : ''} ${
            cursorPhase === 'moving' ? styles.cursorMoving : ''
          } ${cursorPhase === 'pressed' ? styles.cursorPressed : ''}`}
          style={{ transform: cursorTransform }}
        >
          <svg viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5 3 L5 22 L10 17 L13.5 24 L16.5 22.5 L13 16 L20 16 Z"
              fill="#ffffff"
              stroke="#000000"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  )
}
